import React, { useMemo } from 'react';

import {
  Box,
  Button,
  Chip,
  Stack,
  TextField,
  Typography,
  styled,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { RhfSelect } from '@cnx-saas/common-fe';
import { UserType } from '@cnx-saas/common';
import { getUserTypes, InvitedUsers } from '../type';

type InviteUserFormProps = {
  onClose(): void;
  onSubmit(invitedUsers: InvitedUsers): void;
};

export const InviteUserForm = ({
  onClose,
  onSubmit: parentHandleSubmit,
}: InviteUserFormProps) => {
  const { t } = useTranslation();
  const { control, handleSubmit } = useForm<InvitedUsers>({
    defaultValues: { emails: [], accountType: UserType.TEAM_ADMIN },
  });
  const [email, setEmail] = React.useState('');
  const [isError, setError] = React.useState(false);
  const [emailList, setEmailList] = React.useState<string[]>([]);

  const userTypes = useMemo(() => getUserTypes(), []);

  const handleAddEmail = () => {
    if (email && !emailList.includes(email)) {
      setError(false);
      setEmailList([...emailList, email]);
      setEmail('');
    }
  };

  const handleRemoveEmail = (emailToRemove: string) => {
    setEmailList(emailList.filter((e) => e !== emailToRemove));
  };
  const onSubmit = (data: InvitedUsers) => {
    if (emailList.length === 0) {
      setError(true);
    } else {
      setError(false);
      parentHandleSubmit({ ...data, emails: emailList });
    }
  };
  return (
    <Root onSubmit={handleSubmit(onSubmit)}>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
          <TextField
            label={t('userManagement.invitation.emailLabel', {
              defaultValue: 'Email Address',
            })}
            variant="outlined"
            size="small"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            sx={{ width: '83%' }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleAddEmail}
            style={{ marginLeft: 10 }}
          >
            {t('userManagement.invitation.addButtonLabel', {
              defaultValue: 'Add',
            })}
          </Button>
        </Box>
        {isError && (
          <Typography color={'red'}>
            {t('userManagement.invitation.error', {
              defaultValue: 'Please add emails',
            })}
          </Typography>
        )}
        <Box
          mt={2}
          mb={2}
          width={400}
          height={emailList.length === 0 ? 0 : 100}
          sx={{ overflowY: 'auto' }}
        >
          {emailList.map((email, index) => (
            <Chip
              key={index}
              label={email}
              onDelete={() => handleRemoveEmail(email)}
              style={{ margin: '5px' }}
            />
          ))}
        </Box>
        <RhfSelect
          control={control}
          name="accountType"
          label={t('userManagement.label.userType', {
            defaultValue: 'Account type',
          })}
          values={Object.values(userTypes)}
          getValueLabel={(userType) =>
            t(`userManagement.label.${userType}`, { defaultValue: userType })
          }
          sx={{ display: 'flex', width: '83%' }}
        />
        <Stack flexDirection="row" justifyContent="end" gap={1}>
          <Button variant="outlined" onClick={onClose}>
            {t('userManagement.form.cancel', {
              defaultValue: 'Cancel',
            })}
          </Button>
          <Button type="submit" disabled={emailList.length === 0}>
            {t('userManagement.form.send', { defaultValue: 'Send' })}
          </Button>
        </Stack>
      </Box>
    </Root>
  );
};

export const Root = styled('form')({
  width: '100%',
});
